/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VideoPlayerModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./index.module.css";
import VideoThumbnail from "../../components/VideoTutorialsSection/VideoThumbnail.jsx";
import { generateAssetUrl } from "../../utils/common.js";

const VideoPlayerModal = ({
  open,
  onClose,
  video,
  section,
  allVideos,
  onVideoSelect,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleClose = () => {
      // Pause video when modal closes
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      onClose();
    };

    const handleCancel = (e) => {
      // Prevent closing on backdrop click - only close via close button
      e.preventDefault();
    };

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    dialog.addEventListener("close", handleClose);
    dialog.addEventListener("cancel", handleCancel);

    if (open) {
      // Only show modal if it's not already open
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
        dialog.removeEventListener("cancel", handleCancel);
        // Only close dialog on cleanup if modal is being closed
        if (!open && dialog.open) {
          dialog.close();
        }
      }
    };
  }, [open, onClose]);

  // Reset video when video changes
  useEffect(() => {
    if (videoRef.current && video) {
      videoRef.current.load(); // Reload video to reset it
      videoRef.current.currentTime = 0;
    }
  }, [video?._id, video?.id]);

  // Get other videos in the same section (excluding current video)
  const getSidebarVideos = () => {
    if (!video || !allVideos || allVideos.length === 0) {
      return [];
    }

    const sectionId = section?._id || section?.id;
    if (!sectionId) {
      return [];
    }

    const currentVideoId = video._id || video.id;

    return allVideos
      .filter((v) => {
        const videoSectionId = v.sectionId;
        const videoId = v._id || v.id;

        // Check if video belongs to the same section
        const sameSection =
          videoSectionId === sectionId ||
          videoSectionId === section?._id ||
          videoSectionId === section?.id;

        // Check if it's not the current video
        const notCurrentVideo = videoId !== currentVideoId;

        return sameSection && notCurrentVideo;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const sidebarVideos = getSidebarVideos();

  const handleVideoClick = (selectedVideo, e) => {
    // Prevent event propagation to avoid closing the modal
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // When clicking a video in sidebar, swap it with the current video
    if (onVideoSelect) {
      onVideoSelect(selectedVideo);
    }
  };

  // Don't render if modal is not open or video is invalid
  if (!open || !video || !video.videoUrl) {
    return null;
  }

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            className={styles.closeButton}
            onClick={() => {
              // Pause video when closing
              if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
              }
              if (dialogRef.current) {
                dialogRef.current.close();
              }
              onClose();
            }}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>
            {section?.name || t("components.videoPlayerModal.sectionTitle")}
          </h2>

          <div className={`${styles.contentContainer} ${styles.withSidebar}`}>
            <div className={styles.videoSection}>
              <div className={styles.videoPlayerContainer}>
                <video
                  ref={videoRef}
                  src={generateAssetUrl(video.videoUrl)}
                  controls
                  className={styles.videoPlayer}
                  autoPlay
                />
              </div>

              <div className={styles.videoInfo}>
                {video.name && (
                  <h3 className={styles.videoName}>{video.name}</h3>
                )}
                {video.description && (
                  <p className={styles.videoDescription}>{video.description}</p>
                )}
              </div>
            </div>

            <div className={styles.sidebar}>
              {sidebarVideos.length > 0 ? (
                <div className={styles.videoList}>
                  {sidebarVideos.map((sidebarVideo, index) => (
                    <div
                      key={sidebarVideo._id || sidebarVideo.id || index}
                      className={styles.videoListItem}
                      onClick={(e) => handleVideoClick(sidebarVideo, e)}
                    >
                      <div className={styles.videoListItemThumbnail}>
                        {sidebarVideo.videoUrl ? (
                          <VideoThumbnail
                            videoUrl={sidebarVideo.videoUrl}
                            thumbnailSecond={sidebarVideo.thumbnailSecond || 0}
                          />
                        ) : (
                          <div className={styles.thumbnailPlaceholder}>
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 5V19L19 12L8 5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateText}>
                    {t("components.videoPlayerModal.noOtherVideos")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default VideoPlayerModal;
